import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { redirectToCenterHome } from '@/utils/navigation';

interface User {
    studentNumber: number;
    name: string;
    fixedStudySeats: FixedStudySeat[];
    todayBusReservation: CurrentBusReservation | null;
    todayDinnerReservation: CurrentDinnerReservation | null;
    serviceAccess: ServiceAccess;
}

interface ServiceAccess {
    studyCafeEnabled: boolean;
    busEnabled: boolean;
    dinnerEnabled: boolean;
}

interface FixedStudySeat {
    seatId: number;
    seatName: string;
    fixedAccountId: number;
}

interface CurrentBusReservation {
    reservationId: number;
    busName: string;
    departureTime: string;
}

interface CurrentDinnerReservation {
    reservationId: number;
    dinnerTime: string;
}

interface LoginResult {
    success: boolean;
    message?: string;
}

interface AuthContextType {
    user: User | null;
    isLoggedIn: boolean;
    isLoading: boolean;
    login: (studentNumber: number, password: string) => Promise<LoginResult>;
    logout: () => Promise<void>;
    checkAuth: (options?: { showLoading?: boolean }) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const applyUserState = useCallback((nextUser: User | null) => {
        setUser((prevUser) => {
            if (prevUser === null && nextUser === null) {
                return prevUser;
            }

            if (
                prevUser !== null &&
                nextUser !== null &&
                prevUser.studentNumber === nextUser.studentNumber &&
                prevUser.name === nextUser.name &&
                JSON.stringify(prevUser.fixedStudySeats) === JSON.stringify(nextUser.fixedStudySeats) &&
                JSON.stringify(prevUser.todayBusReservation) === JSON.stringify(nextUser.todayBusReservation) &&
                JSON.stringify(prevUser.todayDinnerReservation) === JSON.stringify(nextUser.todayDinnerReservation) &&
                JSON.stringify(prevUser.serviceAccess) === JSON.stringify(nextUser.serviceAccess)
            ) {
                return prevUser;
            }

            return nextUser;
        });
    }, []);

    const checkAuth = useCallback(async (
        options: { showLoading?: boolean } = {}
    ): Promise<User | null> => {
        const { showLoading = true } = options;

        if (showLoading) {
            setIsLoading(true);
        }

        try {
            const response = await fetch('/api/center/auth/me');
            if (response.ok) {
                const data = await response.json();
                if (data.isLoggedIn) {
                    const nextUser: User = {
                        studentNumber: data.studentNumber,
                        name: data.name,
                        fixedStudySeats: data.fixedStudySeats ?? [],
                        todayBusReservation: data.todayBusReservation ?? null,
                        todayDinnerReservation: data.todayDinnerReservation ?? null,
                        serviceAccess: {
                            studyCafeEnabled: data.serviceAccess?.studyCafeEnabled !== false,
                            busEnabled: data.serviceAccess?.busEnabled !== false,
                            dinnerEnabled: data.serviceAccess?.dinnerEnabled !== false,
                        },
                    };
                    applyUserState(nextUser);
                    return nextUser;
                } else {
                    applyUserState(null);
                    return null;
                }
            }

            applyUserState(null);
            return null;
        } catch (error) {
            console.error('Failed to check auth:', error);
            applyUserState(null);
            return null;
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }

        return null;
    }, [applyUserState]);

    // 페이지 로드 시 인증 상태 확인
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (studentNumber: number, password: string): Promise<LoginResult> => {
        try {
            const response = await fetch('/api/center/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ studentNumber, password }),
            });

            if (response.ok) {
                await checkAuth({ showLoading: false });
                return { success: true };
            }

            let message = '학번 또는 비밀번호가 올바르지 않습니다.';
            try {
                const errorData = await response.json();
                if (typeof errorData?.message === 'string' && errorData.message.trim()) {
                    message = errorData.message;
                }
            } catch {
                // ignore parse error
            }

            return { success: false, message };
        } catch (error) {
            console.error('Login failed:', error);
            return {
                success: false,
                message: '로그인 중 오류가 발생했습니다.'
            };
        }
    }, [checkAuth]);

    const logout = useCallback(async () => {
        try {
            await fetch('/api/center/auth/logout', { method: 'POST' });
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            applyUserState(null);
            redirectToCenterHome();
        }
    }, [applyUserState]);

    const authContextValue = useMemo<AuthContextType>(() => ({
        user,
        isLoggedIn: !!user,
        isLoading,
        login,
        logout,
        checkAuth
    }), [user, isLoading, login, logout, checkAuth]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
